import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { destinations } from '../data/mock';

export const Header = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDestinationsOpen, setIsDestinationsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'destinations', label: t('nav.destinations'), hasDropdown: true },
    { key: 'experiences', label: t('nav.experiences') },
    { key: 'yachts', label: t('nav.yachts') },
    { key: 'tailorMade', label: t('nav.tailorMade') },
    { key: 'about', label: t('nav.about') },
    { key: 'contact', label: t('nav.contact') },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-stone-900/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="#"
            onClick={(e) => { e.preventDefault(); onNavigate('home'); }}
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-3"
          >
            <div className="relative">
              <div className={`text-2xl font-serif tracking-wide ${
                isScrolled ? 'text-stone-800' : 'text-white'
              }`}>
                <span className="font-light">Bali</span>
                <span className="font-semibold text-amber-600"> Lux</span>
                <span className="font-light"> Vacances</span>
              </div>
              <div className={`text-[10px] uppercase tracking-[0.3em] mt-0.5 ${
                isScrolled ? 'text-stone-500' : 'text-white/70'
              }`}>
                Luxury Travel Indonesia
              </div>
            </div>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.key}
                className="relative"
                onMouseEnter={() => item.hasDropdown && setIsDestinationsOpen(true)}
                onMouseLeave={() => item.hasDropdown && setIsDestinationsOpen(false)}
              >
                <motion.button
                  whileHover={{ y: -1 }}
                  onClick={() => onNavigate(item.key)}
                  className={`px-4 py-2 text-sm font-medium tracking-wide transition-colors flex items-center gap-1 ${
                    isScrolled
                      ? 'text-stone-700 hover:text-amber-700'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                  {item.hasDropdown && <ChevronDown className="w-3 h-3" />}
                </motion.button>

                {/* Destinations Mega Menu */}
                {item.hasDropdown && (
                  <AnimatePresence>
                    {isDestinationsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-0 w-[600px] bg-white/98 backdrop-blur-md rounded-xl shadow-2xl shadow-stone-900/10 p-6 grid grid-cols-3 gap-4"
                      >
                        {destinations.map((dest) => (
                          <motion.button
                            key={dest.id}
                            whileHover={{ x: 4 }}
                            onClick={() => onNavigate(`destination-${dest.id}`)}
                            className="text-left p-3 rounded-lg hover:bg-amber-50 transition-colors group"
                          >
                            <div className="font-medium text-stone-800 group-hover:text-amber-700 transition-colors">
                              {dest.name}
                            </div>
                            <div className="text-xs text-stone-500 mt-0.5">
                              {dest.tagline}
                            </div>
                          </motion.button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
            
            <div className="ml-2 pl-4 border-l border-stone-200/50">
              <LanguageSwitcher />
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-stone-700 hover:bg-stone-100' : 'text-white hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-stone-100 overflow-hidden"
          >
            <div className="px-4 py-6 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    onNavigate(item.key);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-stone-700 hover:bg-amber-50 hover:text-amber-700 rounded-lg transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 border-t border-stone-100">
                <LanguageSwitcher />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
